import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface LoadingSkeletonProps {
  variant:
    | 'stats-grid'
    | 'table'
    | 'card-grid'
    | 'detail'
    | 'channel-dashboard'
    | 'campaign-group-detail'
    | 'review-detail';
  count?: number;
  /**
   * Presentational only (Phase 8): `'branded'` inside a magic-link shell. Default
   * `'portal'`. Branded geometry exists for the variants a magic-link surface
   * renders — `table` (FeedbackThread), `detail` (ConceptDetailView) and
   * `review-detail` (ContentDetailView); other variants keep their portal blocks.
   * Branded also exposes an accessible loading status (U7) and hides the
   * decorative blocks from assistive tech.
   */
  appearance?: 'portal' | 'branded';
  className?: string;
}

// Branded card block: `--bs-surface` with a decorative `--bs-stroke` hairline.
// ss-colour-refresh Phase 7 (master § Palette): the bars on it are the explicit
// `--bs-surface-raised` inner-panel role, not the bridged `bg-muted`. Both are
// decorative and aria-hidden; `__tests__/LoadingSkeleton.test.tsx` pins the roles.
const BRANDED_BLOCK = 'bg-bs-surface border border-bs-stroke rounded-xl';

/** A skeleton bar; the branded arm swaps the bridged `bg-muted` for `surface-raised`. */
function Bar({ branded, className }: { branded: boolean; className: string }) {
  return <Skeleton className={cn(className, branded && 'bg-bs-surface-raised')} />;
}

function StatsGridSkeleton() {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="bg-white border rounded-xl p-4">
          <Skeleton className="h-3 w-20 mb-2" />
          <Skeleton className="h-7 w-16 mb-1" />
          <Skeleton className="h-3 w-24" />
        </div>
      ))}
    </div>
  );
}

function TableSkeleton({ count = 5, branded = false }: { count: number; branded?: boolean }) {
  return (
    <div
      className={
        branded ? `${BRANDED_BLOCK} overflow-hidden` : 'bg-white border rounded-xl overflow-hidden'
      }
    >
      <div className={branded ? 'px-4 py-3 border-b border-bs-stroke' : 'px-4 py-3 border-b'}>
        <Bar branded={branded} className="h-4 w-32" />
      </div>
      <div className={branded ? undefined : 'divide-y'}>
        {Array.from({ length: count }).map((_, i) => (
          <div
            key={i}
            className={
              branded
                ? 'px-4 py-3 flex items-center gap-3 border-b border-bs-stroke last:border-b-0'
                : 'px-4 py-3 flex items-center gap-3'
            }
          >
            <Bar branded={branded} className="h-8 w-8 rounded-full" />
            <div className="flex-1 space-y-1.5">
              <Bar branded={branded} className="h-3 w-40" />
              <Bar branded={branded} className="h-2.5 w-24" />
            </div>
            <Bar branded={branded} className="h-5 w-20 rounded-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

function CardGridSkeleton({ count = 6 }: { count: number }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white border rounded-xl p-4">
          <Skeleton className="h-32 w-full rounded-lg mb-3" />
          <Skeleton className="h-4 w-3/4 mb-2" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      ))}
    </div>
  );
}

/** No card of its own: its branded callers place it inside their `--bs-surface` card. */
function DetailSkeleton({ branded = false }: { branded?: boolean }) {
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Bar branded={branded} className="h-10 w-10 rounded-full" />
        <div className="space-y-1.5">
          <Bar branded={branded} className="h-5 w-48" />
          <Bar branded={branded} className="h-3 w-32" />
        </div>
      </div>
      <Bar branded={branded} className="h-64 w-full rounded-xl" />
      <div className="space-y-2">
        <Bar branded={branded} className="h-3 w-full" />
        <Bar branded={branded} className="h-3 w-5/6" />
        <Bar branded={branded} className="h-3 w-3/4" />
      </div>
    </div>
  );
}

/** Mirrors ChannelDashboardPage loaded layout: breadcrumb + header + 2-col overview card + summary card + tabs */
function ChannelDashboardSkeleton() {
  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-3 w-3 rounded" />
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-3 w-3 rounded" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-3 w-3 rounded" />
        <Skeleton className="h-4 w-24" />
      </div>
      {/* Page header: title + badge */}
      <div className="flex items-center gap-2 flex-wrap">
        <Skeleton className="h-7 w-56" />
        <Skeleton className="h-5 w-16 rounded-full" />
      </div>
      {/* 2-col overview card */}
      <div className="bg-white border rounded-xl p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="space-y-3">
            <Skeleton className="h-4 w-28" />
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex gap-2">
                <Skeleton className="h-3 w-28 shrink-0" />
                <Skeleton className="h-3 w-20" />
              </div>
            ))}
          </div>
          <div className="space-y-3">
            <Skeleton className="h-4 w-16" />
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex gap-2">
                <Skeleton className="h-3 w-28 shrink-0" />
                <Skeleton className="h-3 w-16" />
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Summary widgets row */}
      <div className="grid grid-cols-2 gap-4">
        <Skeleton className="h-24 rounded-xl" />
        <Skeleton className="h-24 rounded-xl" />
      </div>
      {/* Tab bar + content placeholder */}
      <div>
        <div className="flex gap-1 mb-4">
          <Skeleton className="h-9 w-16 rounded-md" />
          <Skeleton className="h-9 w-20 rounded-md" />
          <Skeleton className="h-9 w-20 rounded-md" />
        </div>
        <Skeleton className="h-48 w-full rounded-xl" />
      </div>
    </div>
  );
}

/** Mirrors CampaignGroupDetailPage loaded layout: header + metadata card + channels table */
function CampaignGroupDetailSkeleton() {
  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex justify-between items-start">
        <div className="space-y-1.5">
          <Skeleton className="h-7 w-64" />
          <Skeleton className="h-4 w-32" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-8 w-36 rounded-md" />
          <Skeleton className="h-8 w-28 rounded-md" />
          <Skeleton className="h-8 w-16 rounded-md" />
        </div>
      </div>
      {/* Metadata card */}
      <div className="bg-white border rounded-xl p-5">
        <div className="flex flex-wrap gap-x-8 gap-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-1.5">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-5 w-20 rounded-full" />
            </div>
          ))}
        </div>
      </div>
      {/* Channels table */}
      <div>
        <Skeleton className="h-5 w-20 mb-3" />
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="px-6 py-3 border-b flex gap-4">
            {['Name', 'Type', 'Status', 'Posts', 'Actions'].map((h) => (
              <Skeleton key={h} className="h-3 w-12" />
            ))}
          </div>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="px-6 py-4 border-b last:border-0 flex items-center gap-4">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-5 w-20 rounded-full" />
              <Skeleton className="h-4 w-8" />
              <Skeleton className="h-7 w-16 rounded-md" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Mirrors client ReviewDetailPage loaded layout: breadcrumb + 2-col (media + right panel) */
function ReviewDetailSkeleton({ branded = false }: { branded?: boolean }) {
  return (
    <div>
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 mb-4">
        <Bar branded={branded} className="h-4 w-24" />
        <Bar branded={branded} className="h-3 w-3 rounded" />
        <Bar branded={branded} className="h-4 w-40" />
      </div>
      <div className="flex flex-col lg:flex-row gap-6 mt-2">
        {/* Left: media preview */}
        <div className="flex-1 min-w-0 space-y-4">
          <div
            className={
              branded
                ? `${BRANDED_BLOCK} overflow-hidden`
                : 'bg-white border rounded-xl overflow-hidden'
            }
          >
            <div
              className={branded ? 'px-4 py-3.5 border-b border-bs-stroke' : 'px-4 py-3.5 border-b'}
            >
              <Bar branded={branded} className="h-4 w-32" />
            </div>
            <div className="p-4">
              <Bar branded={branded} className="aspect-video w-full rounded-lg" />
            </div>
          </div>
        </div>
        {/* Right: action panel */}
        <div className="w-full lg:w-[380px] shrink-0 space-y-4">
          <div
            className={
              branded
                ? `${BRANDED_BLOCK} p-4 space-y-3`
                : 'bg-white border rounded-xl p-4 space-y-3'
            }
          >
            <Bar branded={branded} className="h-9 w-full rounded-md" />
            <Bar branded={branded} className="h-9 w-full rounded-md" />
            <Bar branded={branded} className="h-9 w-full rounded-md" />
          </div>
          <div className={branded ? `${BRANDED_BLOCK} p-4` : 'bg-white border rounded-xl p-4'}>
            <Bar branded={branded} className="h-4 w-32 mb-3" />
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex gap-2">
                  <Bar branded={branded} className="h-3 w-24 shrink-0" />
                  <Bar branded={branded} className="h-3 w-28" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function LoadingSkeleton({
  variant,
  count = 5,
  appearance = 'portal',
  className,
}: LoadingSkeletonProps) {
  const branded = appearance === 'branded';
  const blocks = (
    <>
      {variant === 'stats-grid' && <StatsGridSkeleton />}
      {variant === 'table' && <TableSkeleton count={count} branded={branded} />}
      {variant === 'card-grid' && <CardGridSkeleton count={count} />}
      {variant === 'detail' && <DetailSkeleton branded={branded} />}
      {variant === 'channel-dashboard' && <ChannelDashboardSkeleton />}
      {variant === 'campaign-group-detail' && <CampaignGroupDetailSkeleton />}
      {variant === 'review-detail' && <ReviewDetailSkeleton branded={branded} />}
    </>
  );

  if (branded) {
    // `DetailSkeleton` has no card block of its own, so only the wrapper
    // semantics and the bar role differ for it.
    return (
      <div className={cn(className)} role="status">
        <span className="sr-only">Loading…</span>
        <div aria-hidden="true">{blocks}</div>
      </div>
    );
  }

  return <div className={cn(className)}>{blocks}</div>;
}
